/**
 * React Query hooks da carteira de IA — `/v1/ai-wallet/*` (IA como serviço S2).
 *
 * Só leitura: não há mutation aqui de propósito. Recarga (`credit`) e correção (`adjust`)
 * nascem no console da plataforma (`admin-lume`, S5) — o tenant vê o próprio dinheiro,
 * não o move.
 *
 * `enabled` é parâmetro em todos: o gate de leitura desta superfície é o `DomainSection`
 * do hub (capability `ai-billing-read`), mas a seção também é montável fora dele, e
 * disparar 3 requests que voltariam 403 seria ruído — mesmo contrato que
 * `use-ai-config.ts` já usa (`useAiAgents(canManage)`).
 *
 * Dinheiro não se cacheia com folga: `staleTime` curto (10s) porque o saldo muda a cada
 * invocação de IA e um número velho na tela é exatamente o que este produto não pode dar.
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type { WalletContractWire, WalletEntriesPageWire, WalletSnapshotWire } from './types';
import type { UseQueryResult } from '@tanstack/react-query';

export const AI_WALLET_QUERY_ROOT = ['admin', 'ai-wallet'] as const;
export const AI_WALLET_SNAPSHOT_QUERY_KEY = [...AI_WALLET_QUERY_ROOT, 'snapshot'] as const;
export const AI_WALLET_ENTRIES_QUERY_KEY = [...AI_WALLET_QUERY_ROOT, 'entries'] as const;
export const AI_WALLET_CONTRACT_QUERY_KEY = [...AI_WALLET_QUERY_ROOT, 'contract'] as const;

const SALDO_STALE_MS = 10_000;

export function useAiWallet(enabled = true): UseQueryResult<WalletSnapshotWire> {
  return useQuery({
    queryKey: AI_WALLET_SNAPSHOT_QUERY_KEY,
    queryFn: () => api.getAiWallet(),
    enabled,
    staleTime: SALDO_STALE_MS,
  });
}

/**
 * Preço efetivo do tenant (GX1): negociação + tabela global, uma linha por modelo. Nunca `null`
 * — tenant sem negociação vem com `exception: null` e as linhas da tabela.
 */
export function useAiWalletContract(enabled = true): UseQueryResult<WalletContractWire> {
  return useQuery({
    queryKey: AI_WALLET_CONTRACT_QUERY_KEY,
    queryFn: () => api.getAiWalletContract(),
    enabled,
    // Preço muda por reajuste contratual, não por uso — 5min é folgado e honesto.
    staleTime: 5 * 60 * 1000,
  });
}

/** Extrato paginado por cursor. Use `data.pages.flatMap(p => p.items)` para a lista acumulada. */
export function useAiWalletEntries(
  enabled = true,
): ReturnType<
  typeof useInfiniteQuery<
    WalletEntriesPageWire,
    Error,
    { pages: WalletEntriesPageWire[]; pageParams: unknown[] },
    readonly unknown[],
    string | undefined
  >
> {
  return useInfiniteQuery({
    queryKey: AI_WALLET_ENTRIES_QUERY_KEY,
    queryFn: ({ pageParam }) => api.listAiWalletEntries(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    staleTime: SALDO_STALE_MS,
  });
}
